import { ChangeDetectionStrategy, Component, input, inject, computed } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';

import { PermissionService } from '../../services/permission.service';
import { navTabsFor } from './nav-tabs';
import { filter, map, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-navbar',
  standalone: true,
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule],
})
export class NavbarComponent {
  public readonly activeTab = input<string>('activeTab');
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);

  readonly selectedPermission = this.permissionService.selectedPermission;

  // Track current route for active tab highlighting (pathname only, ignoring query params)
  readonly currentRoute = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => {
        const url = (event as NavigationEnd).url;
        // Extract pathname only, ignoring query parameters and fragments
        try {
          return new URL(url, window.location.origin).pathname;
        } catch {
          // Fallback: split on ? and # to handle relative URLs
          return url.split('?')[0].split('#')[0];
        }
      }),
      startWith((() => {
        try {
          return new URL(this.router.url, window.location.origin).pathname;
        } catch {
          return this.router.url.split('?')[0].split('#')[0];
        }
      })())
    )
  );

  // The tab set itself lives in nav-tabs.ts because the breadcrumb builds on
  // the same list — see the note there.
  readonly visibleTabs = computed(() =>
    navTabsFor(this.selectedPermission(), this.permissionService.selectedAcademicOfficer())
  );

  // Determine active tab based on current route
  readonly currentActiveTab = computed(() => {
    const route = this.currentRoute();
    const tabs = this.visibleTabs();
    
    if (!route || !tabs.length) return null;

    // Find the tab that matches the current route (exact match or sub-route)
    const matchingTab = tabs.find(tab => {
      // Exact match
      if (route === tab.route) return true;
      
      // Check for sub-routes - tab route should be a prefix of current route
      if (route.startsWith(tab.route + '/')) return true;
      
      // Special case: academic officer routes (dynamic IDs)
      if (tab.route.startsWith('/academic-officer/') && route.startsWith('/academic-officer/')) {
        // Extract the officer ID from both routes to ensure they match
        const tabOfficerId = tab.route.split('/')[2];
        const routeOfficerId = route.split('/')[2];
        
        // Only match if it's the exact dashboard route, not the profile sub-route
        const tabPath = tab.route.split('/').slice(3); // Everything after /academic-officer/{id}
        const routePath = route.split('/').slice(3); // Everything after /academic-officer/{id}
        
        return tabOfficerId === routeOfficerId && 
               (tabPath.length === 0 && routePath.length === 0); // Both are dashboard routes
      }
      
      return false;
    });

    return matchingTab?.label || null;
  });

  // Check if a specific tab is currently active (non-clickable)
  isTabActive(tabLabel: string): boolean {
    return this.currentActiveTab() === tabLabel;
  }

  // Backward compatibility
  readonly tabs = this.visibleTabs;
}
